import { Component, computed, inject, Signal } from '@angular/core';
import { Observable } from 'rxjs';
import { Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	defaultDoorstroomAvailableGroups,
	DoorstroomMeasure,
	ExportDataOptions,
	xAggExcept,
} from '../../services/data.service';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { maxDeelVanJaar, maxOverMapped, maxSumOfStacksIf, noAgg0, sumOver } from '../../services/aggregation';
import { att, att0, count_records, percOfTotal } from '../../services/measures';
import { defaultDoorstroomActueelFilters, defaultDoorstroomHistorieFilters } from '../../services/filter-config';
import { DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { range } from 'lodash-es';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { Axis, createYAxis } from '../../services/axis';
import { DashboardVariant, Eenheid } from '../../services/weergave-opties';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { BarInfo } from '../../services/stacked-bars';
import { PsName } from '../../services/page-state.service';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { formatPercent } from '@angular/common';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface LeerlingI extends Attributes {
	ds_m_nr_weging: number;
	'ds_fk_lb_van.lb_is_pl_voorlopig': string;
	xa: { [nr: number]: { ds_m_nr_weging: number } };
	ds_m_nr_leerlingen: number;
}

interface LeerlingA extends Attributes {
	max: number;
	ds_m_nr_weging: number;
	ds_is_lb_voorlopig: number;
	maxPctJaar: number;
	ds_m_nr_leerlingen: number;
}

const historieSchooljaarGroup: AttrPath = ['ds_nm_schooljaar_van'];

@Component({
	selector: 'app-leerling',
	templateUrl: './leerling.component.html',
	styleUrls: ['./leerling.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		WeergaveOptieComponent,
		FilterBarComponent,
		ToggleButtonsComponent,
	],
})
export class LeerlingComponent extends BarchartTableConfig<LeerlingI, LeerlingA> {
	private readonly dataService = inject(DataService);

	fixedSubGroups: AttrPath[] = [['ds_fk_lb_van', 'lb_is_pl_voorlopig']];

	readonly historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	readonly historieSubgroups = computed(() => [
		...this.groeperingService.huidigeGroeperingen().slice(-1),
		historieSchooljaarGroup,
		...this.fixedSubGroups,
	]);

	override availableGroups: AttrPath[] = [...defaultDoorstroomAvailableGroups, ['ds_fk_ilt_van', 'ilt_co_ilt']];

	actueelFilters: FilterName[] = [...defaultDoorstroomActueelFilters, 'x_peildatum'];

	historieFilters: FilterName[] = [...defaultDoorstroomHistorieFilters, 'x_peildatum'];

	permanentFilterExpressions = [
		// Voorkom dubbelingen door doorstroom-record van relevante plaatsing naar zowel niet- als wel-relevante plaatsing
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nr_weging'], 0, '>'),
	];

	variant = this.qp.signal('variant');

	eenheid = this.qp.signal('eenheid');

	factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		const xa = this.variant() === DashboardVariant.ACTUEEL ? [] : [xAggExcept(options.g!, historieSchooljaarGroup)];
		return this.dataService.getDoorstroomData({ ...options, m: [DoorstroomMeasure.LEERLINGEN], r: range(0, this.getRowLevel() + 1), xa });
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	protected override singleAggregators = {
		ds_m_nr_weging: sumOver<'ds_m_nr_weging', LeerlingI, number>('ds_m_nr_weging'),
		ds_is_lb_voorlopig: maxOverMapped<LeerlingI>((v) => Number(v['ds_fk_lb_van.lb_is_pl_voorlopig'])),
		maxPctJaar: maxDeelVanJaar('ds_m_nr_weging'),
		ds_m_nr_leerlingen: noAgg0<'ds_m_nr_leerlingen', LeerlingI>('ds_m_nr_leerlingen'),
	};

	protected override multiAggregators = [
		maxSumOfStacksIf<'max', 'ds_m_nr_weging', LeerlingA, LeerlingI>(
			'max',
			'ds_m_nr_weging',
			() => this.groeperingService.huidigeAantalGroeperingen() + (this.variant() === DashboardVariant.HISTORIE ? 1 : 0),
			() => true
		),
	];

	override createMeasureColumns(context: DashboardContext<LeerlingI, LeerlingA, LeerlingComponent>): ColumnDef<DataRow<LeerlingA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		return [
			this.createLeerlingColumn('Leerlingen', att('ds_m_nr_weging'), { context }, count_records),
			this.createLeerlingColumn(
				'Percentage',
				percOfTotal('ds_m_nr_weging', 'ds_m_nr_weging'),
				{ context, dataType: 'percentage' },
				count_records
			),
		];
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	override enrichTableModel(_context: DashboardContext<LeerlingI, LeerlingA, LeerlingComponent>, tableModel: TableModel<DataRow<LeerlingA>>) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	override createYAxis(context: DashboardContext<LeerlingI, LeerlingA, LeerlingComponent>): Axis {
		if (!context.dataRoot) return super.createYAxis(context);
		if (this.eenheid() === Eenheid.AANTAL) {
			return createYAxis([0, context.dataRoot.a.max]);
		} else {
			return createYAxis([0, context.dataRoot.a.maxPctJaar], 5, (val) => formatPercent(val, 'nl_NL'));
		}
	}

	override createXAxis(context: DashboardContext<LeerlingI, LeerlingA, LeerlingComponent>): Axis {
		return { min: 0, max: context.dataRoot!.a.max, ticks: [] };
	}

	getRowLevel() {
		return this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.historieGroups().length;
	}

	override getBarchartQty(path: Path<LeerlingA, number[]>) {
		if ((this.variant() === DashboardVariant.HISTORIE && this.eenheid()) == Eenheid.PERCENTAGE) return att0('maxPctJaar')(path);
		else return att0('ds_m_nr_weging')(path);
	}

	override makeBar(attrs: LeerlingI, path: Path<LeerlingA, number[]>, context: DashboardContext<LeerlingI, LeerlingA, LeerlingComponent>): BarInfo {
		const { 'ds_fk_lb_van.lb_is_pl_voorlopig': ds_is_lb_voorlopig, ds_m_nr_weging } = attrs;

		return {
			...super.makeBar(attrs, path, context),
			description: 'Leerlingen',
			className: ds_is_lb_voorlopig == '1' ? 'dashboard-prognose' : 'dashboard-default',
			tooltip: [
				{ label: 'Prognose', value: ds_is_lb_voorlopig == '1' ? 'Ja' : 'Nee' },
				{ label: 'Leerlingen', value: `${ds_m_nr_weging}` },
			],
		};
	}

	override onContextCreated(context: DashboardContext<LeerlingI, LeerlingA, LeerlingComponent>) {
		this.pageStateService.dispatch(PsName.prognose, String(Boolean(context.dataRoot?.a.ds_is_lb_voorlopig)));
	}

	readonly partitionMeasure: Signal<PartitionMeasure<LeerlingA>> = computed(() => {
		if (this.eenheid() === Eenheid.AANTAL)
			return {
				type: 'number',
				getValue: att('ds_m_nr_weging'),
				format: '1.0-2',
			};
		else
			return {
				type: 'percentage',
				getValue: att('maxPctJaar'),
			};
	});

	override getSelectionConfig(context: DataRequestContext<LeerlingA>): SelectionConfig<LeerlingA> | undefined {
		return {
			...super.getSelectionConfig(context)!,
			getSize: att('ds_m_nr_leerlingen'),
			extraFilters: [],
		};
	}

	protected readonly Eenheid = Eenheid;
	protected readonly DashboardVariant = DashboardVariant;
}
